import { createBlocksClient } from "@seliseblocks/client";
import { BLOCKS_API_URL, PROJECT_KEY } from "@/src/services/blocks/config";
import apiClient from "@/src/services/api/axios";
import gqlClient from "@/src/services/api/graphql.client";
import { GET_POST_CARDS_QUERY } from "@/src/services/api/posts/posts.query";
import type { Post } from "@/src/models/post.model";
import {
  assertVersion,
  enabled,
  safeFollowingWhere,
  type FollowControl,
  type FollowingList,
  type FollowingCards,
  type FollowerNotice,
  type AuthorCounts,
} from "./contract";
const id = process.env.EXPO_PUBLIC_FOLLOWING_FUNCTION_ID ?? "";
// A lost/late native receipt is uncertain, not a safe reason to replay a toggle.
// The request may still commit; callers reconcile state before another mutation.
async function nativeClient() {
  const client = await gqlClient();
  return {
    request: async <T = any>(document: any, variables: any): Promise<T> => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        return await Promise.race([
          client.request<T>(document, variables),
          new Promise<never>((_resolve, reject) => {
            timer = setTimeout(
              () => reject(new Error("FOLLOWING_UNCERTAIN")),
              5000,
            );
          }),
        ]);
      } finally {
        if (timer) clearTimeout(timer);
      }
    },
  };
}
export const requestId = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(16)), (n) =>
    n.toString(16).padStart(2, "0"),
  ).join("");
export async function reader<
  T extends {
    schemaVersion: number;
  },
>(body: Record<string, unknown>): Promise<T> {
  if (!enabled) throw new Error("FOLLOWING_UNAVAILABLE");
  const blocks = createBlocksClient({
    apiUrl: BLOCKS_API_URL,
    xBlocksKey: PROJECT_KEY,
    fetch: async (url, init) => {
      const r = await apiClient.request({
        url: String(url),
        method: init?.method,
        data: init?.body,
        headers: Object.fromEntries(new Headers(init?.headers).entries()),
        timeout: 10000,
      });
      return new Response(JSON.stringify(r.data), {
        status: r.status,
        headers: {
          "content-type": "application/json",
        },
      });
    },
  });
  let value: any = await blocks.http.request(
    `/logic/v4/fn/${encodeURIComponent(id)}`,
    {
      method: "POST",
      body,
      headers: {
        Prefer: "wait=5",
      },
    },
  );
  if (value?.runId && value?.pollToken) {
    const { runId, pollToken } = value;
    for (let i = 0; i < 20; i++) {
      await new Promise((r) => setTimeout(r, 400));
      const result: any = await blocks.http.request(
        `/logic/v4/fn/runs/${encodeURIComponent(runId)}/result`,
        {
          headers: {
            "x-poll-token": pollToken,
          },
        },
      );
      if (String(result.status).toUpperCase() === "SUCCEEDED") {
        value =
          typeof result.result === "string"
            ? JSON.parse(result.result)
            : result.result;
        break;
      }
      if (
        ["FAILED", "TIMEDOUT", "TIMED_OUT", "CANCELLED"].includes(
          String(result.status).toUpperCase(),
        )
      )
        throw new Error("FOLLOWING_UNAVAILABLE");
    }
  }
  if (value?.statusCode) {
    if (value.statusCode >= 400)
      throw new Error(value.body?.code ?? "FOLLOWING_UNAVAILABLE");
    value =
      typeof value.body === "string" ? JSON.parse(value.body) : value.body;
  }
  return assertVersion(value) as T;
}
const fields =
  "ItemId scopeOrgId followerId authorId epochId desiredFollowing appNotificationsEnabled digestEnabled revision requestId";
export async function getControl(
  viewer: string,
  org: string,
  author: string,
): Promise<FollowControl | null> {
  const c = await nativeClient();
  const r: any = await c.request(
    `query($where:followcontrolFilterInput){getfollowcontrols(where:$where,paging:{pageNo:1,pageSize:1}){items{${fields}}}}`,
    {
      where: {
        scopeOrgId: {
          eq: org,
        },
        followerId: {
          eq: viewer,
        },
        authorId: {
          eq: author,
        },
      },
    },
  );
  return r.getfollowcontrols.items[0] ?? null;
}
export async function getControls(
  viewer: string,
  org: string,
): Promise<FollowControl[]> {
  const c = await nativeClient();
  const items: FollowControl[] = [];
  for (let page = 1; page <= 6; page++) {
    const r: any = await c.request(
      `query($where:followcontrolFilterInput,$pageNo:Int){getfollowcontrols(where:$where,paging:{pageNo:$pageNo,pageSize:100}){items{${fields}}}}`,
      {
        where: {
          scopeOrgId: {
            eq: org,
          },
          followerId: {
            eq: viewer,
          },
          desiredFollowing: {
            eq: true,
          },
        },
        pageNo: page,
      },
    );
    items.push(...r.getfollowcontrols.items);
    if (r.getfollowcontrols.items.length < 100) return items;
  }
  throw new Error("FOLLOWING_LIMIT");
}
function requireAck(value: any, key: string, conditional = false): string {
  const ack = value?.[key];
  if (!ack?.acknowledged || (conditional && ack.totalImpactedData !== 1))
    throw new Error(conditional ? "FOLLOWING_CONFLICT" : "FOLLOWING_UNCERTAIN");
  return ack.itemId ?? "";
}
export async function saveControl(
  viewer: string,
  org: string,
  author: string,
  previous: FollowControl | null,
  patch: Partial<
    Pick<
      FollowControl,
      "desiredFollowing" | "appNotificationsEnabled" | "digestEnabled"
    >
  >,
  operation = requestId(),
): Promise<FollowControl> {
  if (!enabled || !viewer || !org || !author || author === viewer)
    throw new Error("FOLLOWING_UNAVAILABLE");
  const c = await nativeClient();
  let epoch = previous?.epochId ?? null;
  if (patch.desiredFollowing === true && !previous?.desiredFollowing) {
    const r: any = await c.request(
      `mutation($input:followepochInsertInput!){insertfollowepoch(input:$input){acknowledged itemId}}`,
      {
        input: {
          scopeOrgId: org,
          followerId: viewer,
          authorId: author,
          activationRequestId: operation,
        },
      },
    );
    epoch = requireAck(r, "insertfollowepoch");
    if (!epoch) throw new Error("FOLLOWING_UNCERTAIN");
  }
  const input = {
    ...(previous
      ? {
          appNotificationsEnabled: previous.appNotificationsEnabled,
          digestEnabled: previous.digestEnabled,
        }
      : {
          appNotificationsEnabled: true,
          digestEnabled: true,
        }),
    ...patch,
    epochId: epoch,
    revision: operation,
    requestId: operation,
  };
  if (previous) {
    const r: any = await c.request(
      `mutation($where:followcontrolFilterInput!,$input:followcontrolUpdateInput!){updatefollowcontrol(where:$where,input:$input){acknowledged totalImpactedData itemId}}`,
      {
        where: {
          ItemId: {
            eq: previous.ItemId,
          },
          revision: {
            eq: previous.revision,
          },
          scopeOrgId: {
            eq: org,
          },
          followerId: {
            eq: viewer,
          },
        },
        input,
      },
    );
    requireAck(r, "updatefollowcontrol", true);
    return {
      ...previous,
      ...input,
    };
  }
  if (!patch.desiredFollowing) throw new Error("FOLLOWING_CONFLICT");
  const r: any = await c.request(
    `mutation($input:followcontrolInsertInput!){insertfollowcontrol(input:$input){acknowledged itemId}}`,
    {
      input: {
        scopeOrgId: org,
        followerId: viewer,
        authorId: author,
        ...input,
        desiredFollowing: true,
      },
    },
  );
  const ItemId = requireAck(r, "insertfollowcontrol");
  return {
    ItemId,
    scopeOrgId: org,
    followerId: viewer,
    authorId: author,
    desiredFollowing: true,
    ...input,
  };
}
export const readState = (authorId: string) =>
  reader<{
    schemaVersion: 1;
    items: FollowingList["items"];
  }>({
    action: "state",
    authorId,
  });
export const readList = (cursor?: string) =>
  reader<FollowingList>({
    action: "state",
    pageSize: 20,
    ...(cursor
      ? {
          cursor,
        }
      : {}),
  });
export const readCounts = (authorIds: string[]) =>
  reader<{
    schemaVersion: 1;
    items: AuthorCounts[];
  }>({
    action: "counts",
    authorIds: authorIds.slice(0, 50),
  });
export const readInbox = (cursor?: string, unreadOnly = false) =>
  reader<{
    schemaVersion: 1;
    items: FollowerNotice[];
    nextCursor: string | null;
    unreadCount: number;
  }>({
    action: "inbox",
    pageSize: 20,
    unreadOnly,
    ...(cursor
      ? {
          cursor,
        }
      : {}),
  });
export async function directCards(
  viewer: string,
  org: string,
  authors: string[],
  pageNo: number,
  search: string,
  sort: "latest" | "hottest" = "latest",
) {
  if (!authors.length) return [] as Post[];
  const c = await nativeClient();
  const r: any = await c.request(GET_POST_CARDS_QUERY, {
    where: safeFollowingWhere(viewer, org, authors, search),
    pageNo,
    pageSize: 5,
    order: [
      {
        field: sort === "hottest" ? "score" : "CreatedDate",
        direction: "DESC",
      },
    ],
  });
  return r.getposts.items as Post[];
}
export const readProtected = (sort: string, cursor?: string, search = "") =>
  reader<FollowingCards>({
    action: "rankedPage",
    sort,
    pageSize: 5,
    search,
    ...(cursor
      ? {
          cursor,
        }
      : {}),
  });
export async function markFollowerRead(
  viewer: string,
  org: string,
  deliveryId: string,
) {
  const c = await nativeClient();
  const r: any = await c.request(
    `mutation($input:followreadreceiptInsertInput!){insertfollowreadreceipt(input:$input){acknowledged itemId}}`,
    {
      input: {
        scopeOrgId: org,
        readerId: viewer,
        deliveryId,
      },
    },
  );
  requireAck(r, "insertfollowreadreceipt");
}
export type FollowingSettings = {
  ItemId: string;
  appNotificationsEnabled: boolean;
  digestEnabled: boolean;
  revision: string;
  featureEnabled: boolean;
};
export async function getSettings(
  viewer: string,
  org: string,
): Promise<FollowingSettings | null> {
  const c = await nativeClient();
  const r: any = await c.request(
    `query($where:followsettingsFilterInput){getfollowsettingss(where:$where,paging:{pageNo:1,pageSize:1}){items{ItemId appNotificationsEnabled digestEnabled revision featureEnabled}}}`,
    {
      where: {
        scopeOrgId: {
          eq: org,
        },
        followerId: {
          eq: viewer,
        },
      },
    },
  );
  return r.getfollowsettingss.items[0] ?? null;
}
export async function saveSettings(
  viewer: string,
  org: string,
  previous: FollowingSettings | null,
  patch: {
    appNotificationsEnabled?: boolean;
    digestEnabled?: boolean;
  },
) {
  const c = await nativeClient();
  const revision = requestId();
  if (previous) {
    const r: any = await c.request(
      `mutation($where:followsettingsFilterInput!,$input:followsettingsUpdateInput!){updatefollowsettings(where:$where,input:$input){acknowledged totalImpactedData}}`,
      {
        where: {
          ItemId: {
            eq: previous.ItemId,
          },
          revision: {
            eq: previous.revision,
          },
          followerId: {
            eq: viewer,
          },
          scopeOrgId: {
            eq: org,
          },
        },
        input: {
          ...patch,
          revision,
        },
      },
    );
    requireAck(r, "updatefollowsettings", true);
  } else {
    const r: any = await c.request(
      `mutation($input:followsettingsInsertInput!){insertfollowsettings(input:$input){acknowledged itemId}}`,
      {
        input: {
          scopeOrgId: org,
          followerId: viewer,
          appNotificationsEnabled: true,
          digestEnabled: true,
          ...patch,
          revision,
        },
      },
    );
    requireAck(r, "insertfollowsettings");
  }
}
/** Capture the authorized unread set before writing receipts: mutating while
 * traversing offset pages would skip rows. The 30-day/20-per-day retention cap
 * bounds this read; an unexpected continuation fails rather than scanning forever. */
export async function markAllFollowersRead(viewer: string, org: string) {
  const ids = new Set<string>();
  let cursor: string | undefined;
  for (let page = 0; page < 31; page++) {
    const result = await readInbox(cursor, true);
    for (const item of result.items) ids.add(item.deliveryId);
    cursor = result.nextCursor ?? undefined;
    if (!cursor) break;
  }
  if (cursor) throw new Error("FOLLOWING_LIMIT");
  const pending = [...ids];
  for (let i = 0; i < pending.length; i += 4)
    await Promise.all(
      pending.slice(i, i + 4).map((id) => markFollowerRead(viewer, org, id)),
    );
}
/** Runtime projections may reject a desired request; read them separately so
 * safe pending cards never wait for membership validation or a Function. */
export async function rejectedAuthors(
  viewer: string,
  org: string,
  controls: FollowControl[],
): Promise<string[]> {
  if (!controls.length) return [];
  const c = await nativeClient(),
    rejected: string[] = [];
  const byAuthor = new Map(
    controls.map((control) => [control.authorId, control]),
  );
  for (let pageNo = 1; pageNo <= 6; pageNo++) {
    const result: any = await c.request(
      `query($where:followstateFilterInput,$pageNo:Int){getfollowstates(where:$where,paging:{pageNo:$pageNo,pageSize:100}){items{authorId epochId requestRevision}}}`,
      {
        where: {
          scopeOrgId: { eq: org },
          followerId: { eq: viewer },
          authorId: { in: [...byAuthor.keys()] },
          validationState: { eq: "rejected" },
        },
        pageNo,
      },
    );
    const states = result.getfollowstates.items;
    for (const state of states) {
      const control = byAuthor.get(state.authorId);
      if (
        control?.revision === state.requestRevision &&
        control?.epochId === state.epochId
      )
        rejected.push(state.authorId);
    }
    if (states.length < 100) return rejected;
  }
  throw new Error("FOLLOWING_LIMIT");
}
